import { useState, useEffect, useCallback } from 'react'
import type { R2ObjectInfo, R2ListResult } from '../types'

export function useR2List(bucket: string, prefix: string) {
  const [result, setResult] = useState<R2ListResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchList = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ bucket })
      if (prefix) params.set('prefix', prefix)
      const res = await fetch(`/api/r2-browser?${params}`)
      if (!res.ok) throw new Error('Failed to list R2 objects')
      const data = await res.json()
      setResult(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [bucket, prefix])

  useEffect(() => {
    fetchList()
  }, [fetchList])

  return { result, loading, error, refetch: fetchList }
}

export interface R2ObjectContent {
  contentType: string
  size: number
  body: string | null     // null for >1MB or binary attachment
  truncated: boolean      // true when 413 hit
  attachment: boolean     // true when Content-Disposition: attachment
  downloadUrl: string     // direct URL for explicit download
}

export function useR2ObjectContent(bucket: string, key: string | null) {
  const [content, setContent] = useState<R2ObjectContent | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!key) {
      setContent(null)
      return
    }

    setLoading(true)
    setError(null)

    async function fetchContent() {
      try {
        const params = new URLSearchParams({ bucket, key: key! })
        const url = `/api/r2-browser/object/content?${params}`
        const res = await fetch(url)

        if (res.status === 413) {
          const meta = await res.json() as { size: number }
          setContent({
            contentType: 'application/octet-stream',
            size: meta.size,
            body: null,
            truncated: true,
            attachment: true,
            downloadUrl: url,
          })
          return
        }
        if (!res.ok) throw new Error(`Object fetch failed: ${res.status}`)

        const contentType = res.headers.get('content-type') ?? 'application/octet-stream'
        const sizeStr = res.headers.get('x-content-length')
        const size = sizeStr ? Number(sizeStr) : 0
        const disp = res.headers.get('content-disposition') ?? ''
        const attachment = disp.includes('attachment')

        const body = attachment ? null : await res.text()
        setContent({ contentType, size, body, truncated: false, attachment, downloadUrl: url })
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error')
      } finally {
        setLoading(false)
      }
    }

    fetchContent()
  }, [bucket, key])

  return { content, loading, error }
}

export function useR2ObjectDetail(bucket: string, key: string | null) {
  const [object, setObject] = useState<R2ObjectInfo | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!key) {
      setObject(null)
      return
    }

    setLoading(true)
    setError(null)

    async function fetchObject() {
      try {
        const params = new URLSearchParams({ bucket, key: key! })
        const res = await fetch(`/api/r2-browser/object?${params}`)
        if (!res.ok) throw new Error('Object not found')
        const data = await res.json()
        setObject(data)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error')
      } finally {
        setLoading(false)
      }
    }

    fetchObject()
  }, [bucket, key])

  return { object, loading, error }
}
